import * as R from 'ramda';
import { reducerFactory } from 'common/store/scope';
import { composeReducers } from '../util';
import { reducer as resourceReducer } from '../resource';
import { reducer as searchReducer } from '../search';

import {
  LOAD,
  LOAD_SUCCESS,
  TOGGLE_SORT,
  PAGINATION_CHANGE,
  SEARCH,
} from './actions';

export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';

export const defaultSort = (property, direction = SORT_DESC) => ({
  sort: {
    direction,
    property,
  },
});

export const defaultState = {
  ...resourceReducer.defaultState,
  total: undefined,
  limit: undefined,
  offset: undefined,
  sort: undefined,
  search: undefined,
};

const reverseSortDirection = direction => (
  direction === SORT_DESC ? SORT_ASC : SORT_DESC
);

export const {
  update,
  loadError,
  loadCancelled,
} = resourceReducer;

export const load = composeReducers(
  resourceReducer.load,
  (state, {
    limit = state.limit,
    offset = state.offset,
    sort = state.sort,
    query = state.query,
  }) => ({
    ...state,
    limit,
    offset,
    sort,
    query,
  }),
);

export const toggleSort = ({ sort = {}, ...state }, { property }) => ({
  ...state,
  sort: {
    property,
    direction: reverseSortDirection(sort.property === property && sort.direction),
  },
});

const getSorter = direction => ({
  [SORT_ASC]: R.ascend,
  [SORT_DESC]: R.descend,
})[direction];

export const sortCaseInsensitive = ({ data, ...state }) => ({
  ...state,
  data: R.sort(
    getSorter(state.sort.direction)(R.compose(
      R.ifElse(R.is(String), R.toLower, R.identity),
      R.prop(state.sort.property),
    )),
    data,
  ),
});

export const paginationChange = (state, { limit, offset }) => ({
  ...state,
  limit,
  offset,
});

const clearOffset = state => ({
  ...state,
  offset: undefined,
});

const setTotal = (state, { total }) => ({
  ...state,
  total,
});

export const loadSuccess = composeReducers(
  (state, { items }) => resourceReducer.loadSuccess(state, { resource: items }),
  setTotal,
);

export const onQueryChange = composeReducers(
  clearOffset,
  searchReducer.onQueryChange,
);

const collectionReducer = (state = defaultState, action) => {
  const reducer = {
    [LOAD]: load,
    [LOAD_SUCCESS]: loadSuccess,
    [TOGGLE_SORT]: toggleSort,
    [PAGINATION_CHANGE]: paginationChange,
    [SEARCH]: onQueryChange,
  }[action.type];

  return reducer ? reducer(state, action) : state;
};

export const factory = namespace => composeReducers(
  resourceReducer.factory(namespace),
  reducerFactory(namespace, searchReducer.default),
  reducerFactory(namespace, collectionReducer),
);

export default factory;
